import { useEffect } from "react";
import { trackEvent } from "@/lib/analytics/track";

/**
 * Auto-instrumentation:
 * - Click delegation: data-track="<name>" data-track-category="cta" data-track-label="..."
 * - Outbound link tracking (any <a href> to external host)
 * - mailto: / tel: tracking
 * - Form submit tracking (any <form data-track-form="name">)
 * - First focus = form_start
 * - Document/PDF download tracking (href ending in .pdf, .zip, .csv etc.)
 */

const DOWNLOAD_EXT = /\.(pdf|zip|csv|xlsx?|docx?|pptx?|mp4|mov)(\?|$)/i;

function findTrackEl(target: EventTarget | null): HTMLElement | null {
  let el = target as HTMLElement | null;
  while (el && el !== document.body) {
    if (el.dataset && (el.dataset.track || el.tagName === "A" || el.tagName === "BUTTON")) return el;
    el = el.parentElement;
  }
  return null;
}

function findAnchor(target: EventTarget | null): HTMLAnchorElement | null {
  let el = target as HTMLElement | null;
  while (el && el !== document.body) {
    if (el.tagName === "A") return el as HTMLAnchorElement;
    el = el.parentElement;
  }
  return null;
}

function findForm(target: EventTarget | null): HTMLFormElement | null {
  let el = target as HTMLElement | null;
  while (el && el !== document.body) {
    if (el.tagName === "FORM") return el as HTMLFormElement;
    el = el.parentElement;
  }
  return null;
}

const formStartFlags = new WeakSet<HTMLFormElement>();

function onClick(e: MouseEvent) {
  // Anchor / outbound / download
  const a = findAnchor(e.target);
  if (a) {
    const href = a.getAttribute("href") || "";
    if (href.startsWith("mailto:")) {
      trackEvent({ name: "mailto_click", category: "contact", label: href.replace("mailto:", "") });
    } else if (href.startsWith("tel:")) {
      trackEvent({ name: "tel_click", category: "contact", label: href.replace("tel:", "") });
    } else if (DOWNLOAD_EXT.test(href) || a.hasAttribute("download")) {
      trackEvent({ name: "download", category: "download", label: href });
    } else {
      try {
        const u = new URL(href, window.location.origin);
        if (u.hostname && u.hostname !== window.location.hostname) {
          trackEvent({ name: "outbound_click", category: "outbound", label: u.hostname + u.pathname });
        }
      } catch {}
    }
  }

  // Explicit data-track
  const tEl = findTrackEl(e.target);
  if (tEl && tEl.dataset.track) {
    trackEvent({
      name: tEl.dataset.track,
      category: tEl.dataset.trackCategory ?? "ui",
      label: tEl.dataset.trackLabel ?? tEl.textContent?.trim().slice(0, 80) ?? null,
    });
  }
}

function onFocusIn(e: FocusEvent) {
  const t = e.target as HTMLElement | null;
  if (!t) return;
  const tag = t.tagName;
  if (tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT") return;
  const form = findForm(t);
  if (!form) return;
  if (formStartFlags.has(form)) return;
  formStartFlags.add(form);
  const name = form.dataset.trackForm || form.getAttribute("name") || form.getAttribute("id") || "unknown";
  trackEvent({ name: "form_start", category: "form", label: name });
}

function onSubmit(e: SubmitEvent) {
  const form = e.target as HTMLFormElement | null;
  if (!form) return;
  const name = form.dataset.trackForm || form.getAttribute("name") || form.getAttribute("id") || "unknown";
  trackEvent({ name: "form_submit", category: "form", label: name });
}

export function EventBoundary() {
  useEffect(() => {
    document.addEventListener("click", onClick, { capture: true, passive: true });
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("submit", onSubmit, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("submit", onSubmit, true);
    };
  }, []);
  return null;
}
